import { WordRunFn } from "../global";

const BOOKMARK_NAME = "__TL_RESTORE_LOCATION__";

/**
 * Represents the current state of the location history.
 * - `unregistered`: Initial state, Word.run not yet registered
 * - `active`: Registered and ready to save/restore locations
 */
export type LocationHistoryPhase = "unregistered" | "active";

/**
 * Manages a circular buffer of document location bookmarks for navigation history.
 * 
 * @example
 * ```typescript
 * const history = new LocationHistory();
 * await history.register(Word.run);
 * 
 * // Save current location
 * await history.push();
 * 
 * // Check if there's a saved location
 * if (history.hasLocation()) {
 *   // Navigate back and remove from history
 *   await history.pop();
 * }
 * ```
 */
export class LocationHistory {
  private static readonly MAX_SIZE = 5;

  private _ids: string[] = ["", "", "", "", ""];
  private _run: WordRunFn | null = null;
  private _phase: LocationHistoryPhase = "unregistered";

  constructor() { }

  /** Returns the current phase of the history */
  get phase(): LocationHistoryPhase {
    return this._phase;
  }

  /**
   * Registers the Word.run function for Office operations and starts a new,
   * activation-scoped history session. Removes all reserved persisted history
   * bookmarks, leaving the history empty and active.
   * Must be called before using push, pop, or removeLast methods.
   * 
   * @param run - Word.run function from Office.js
   */
  async register(run: WordRunFn) {
    this._run = run;
    this.transition("active");

    await run(async (context) => {
      console.log("[LocationHistory] Initializing new empty history session");
      await this._clear(context);
      console.log("[LocationHistory] Initialized with empty history");
    });
  }

  private transition(phase: LocationHistoryPhase): void {
    this._phase = phase;
  }

  private getRun(): WordRunFn {
    if (!this._run) {
      throw new Error("LocationHistory not registered. Call register(Word.run) first.");
    }
    return this._run;
  }

  /**
   * Returns true if there is at least one saved location in the history.
   * Can be called without registering (e.g., to check initial state).
   */
  public hasLocation(): boolean {
    return this.prevIdx() !== undefined;
  }

  /**
   * Saves the current selection (or containing paragraph if selection is empty)
   * to the history. Skips if the paragraph is the same as the previous entry.
   */
  async push() {
    const run = this.getRun();
    await run(async (context) => {
      console.log("[LocationHistory] Pushing current location");
      let selection = context.document.getSelection();
      context.load(selection, { select: ["isEmpty"] });
      await context.sync();

      if (selection.isEmpty) {
        context.load(selection.paragraphs, { select: ["items"] });
        await context.sync();
        selection = selection.paragraphs.items[0].getRange();
      }

      const p = this.prevIdx();
      const c = this.currentIdx();
      const n = this.nextIdx();
      if (await this.set(context, c, selection, p)) {
        await this.delete(context, n);
        console.log("[LocationHistory] Location saved at index", c);
      } else {
        console.log("[LocationHistory] Skipped - same paragraph as previous");
      }
    });
  }

  /**
   * Removes the most recent location from history without navigating to it.
   */
  async removeLast(): Promise<void> {
    const run = this.getRun();
    return await run(async (context) => {
      await this._removeLast(context);
    });
  }

  /**
   * Removes the most recent location from history and navigates to it.
   */
  async pop(): Promise<void> {
    const run = this.getRun();
    await run(async (context) => {
      const range = await this._removeLast(context);
      if (range === undefined) {
        console.log("[LocationHistory] Pop - no location to restore");
        return;
      }
      range.select("Select");
      await context.sync();
      console.log("[LocationHistory] Popped and navigated to location");
    });
  }

  private async _removeLast(
    context: Word.RequestContext
  ): Promise<Word.Range | undefined> {
    let prev: { range: Word.Range; id: string } | undefined = undefined;
    let p = this.prevIdx();
    while (this.hasLocation() && p !== undefined) {
      prev = await this.get(context, p);
      await this.delete(context, p);

      const range = prev?.range;
      if (range === undefined) {
        p = this.prevIdx();
        continue;
      }

      return range;
    }
  }

  /**
   * Returns the paragraph ID of the most recent location, or undefined if empty.
   */
  peek(): string | undefined {
    const idx = this.prevIdx();
    if (idx === undefined) {
      return undefined;
    }
    return this._ids[idx];
  }

  private currentIdx(): number {
    const idx = this._ids.map((x) => x !== "").lastIndexOf(true) + 1;
    if (idx === -1) {
      return 0;
    }

    if (idx >= this._ids.length) {
      return 0;
    }

    return idx;
  }

  private prevIdx(): number | undefined {
    let idx = this.currentIdx();

    if (idx === 0) {
      idx = this._ids.length - 1;
    } else {
      idx--;
    }

    if (this._ids[idx] === "") {
      return undefined;
    }

    return idx;
  }

  private nextIdx(): number {
    let idx = this.currentIdx();

    if (idx === this._ids.length - 1) {
      return 0;
    } else {
      return idx + 1;
    }
  }

  private async get(
    ctx: Word.RequestContext,
    idx: number
  ): Promise<{ range: Word.Range; id: string } | undefined> {
    const bookmarkName = BOOKMARK_NAME + idx.toString();
    const range = ctx.document.getBookmarkRangeOrNullObject(bookmarkName);
    ctx.load(range, { select: ["isNullObject", "paragraphs"], top: 1 });
    await ctx.sync();

    if (range.isNullObject) return undefined;
    ctx.load(range.paragraphs, { top: 1, select: ["items"] });
    await ctx.sync();

    ctx.load(range.paragraphs.items[0], { select: ["uniqueLocalId"] });
    await ctx.sync();

    return { range, id: range.paragraphs.items[0].uniqueLocalId };
  }

  private async set(
    ctx: Word.RequestContext,
    idx: number,
    range: Word.Range,
    previousIdx?: number
  ): Promise<boolean> {
    const bookmarkName = BOOKMARK_NAME + idx.toString();
    ctx.document.deleteBookmark(bookmarkName);
    await ctx.sync();

    ctx.load(range.paragraphs, {
      select: ["items", "uniqueLocalId"],
      top: 1,
    });
    await ctx.sync();

    ctx.load(range.paragraphs, {
      select: ["items", "uniqueLocalId"],
      top: 1,
    });
    await ctx.sync();

    const id = range.paragraphs.items[0].uniqueLocalId;
    if (previousIdx && this._ids[previousIdx] === id) {
      return false;
    }

    this._ids[idx] = range.paragraphs.items[0].uniqueLocalId;
    range.insertBookmark(bookmarkName);
    return true;
  }

  private async delete(ctx: Word.RequestContext, idx: number): Promise<void> {
    const bookmarkName = BOOKMARK_NAME + idx.toString();
    ctx.document.deleteBookmark(bookmarkName);
    await ctx.sync();

    this._ids[idx] = "";
  }

  private async _clear(ctx: Word.RequestContext): Promise<void> {
    for (let i = 0; i < LocationHistory.MAX_SIZE; i++) {
      await this.delete(ctx, i);
      this._ids[i] = "";
    }
  }
}
